import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { setButtonLoading } from "../lib/button_loading"

// NPN Validate button on onboarding Step 3: POSTs the typed NPN and renders the
// result inline, with a "Sign in instead?" link when the NPN is already taken.
//
// Local checks only (format, uniqueness, denylist, lock): no step of the wizard
// asks NIPR anything (#15163, owner 2026-10-01 — the one check is enqueued in
// the background when the wizard is completed).
export default class extends Controller {
  static targets = ["input", "button", "message"]
  static values  = { validateUrl: String }

  async validate() {
    if (!this.hasInputTarget) return
    const npn = this.inputTarget.value.trim()
    if (!npn) {
      const blank = { valid: false, errors: ["Please enter an NPN to validate."] }
      this.show(blank)
      this.announceRecovery(npn, blank)
      // client-error, NOT error: an empty field is ordinary form friction, so it
      // goes through the throttled path that only escalates on repeat. Firing the
      // immediate event here would pop the support prompt on the first click of
      // Validate with nothing typed.
      this.announceError(blank, "client-error")
      return
    }

    this.setButton(true)
    this.clearMessage()

    try {
      const response = await post(this.validateUrlValue, {
        body: { npn_id: npn },
        responseKind: "json"
      })
      const data = await response.json
      this.show(data)
      this.announceRecovery(npn, data)
      this.announceError(data)
    } catch (err) {
      const failed = { valid: false, errors: ["Validation request failed. Please try again."] }
      this.show(failed)
      this.announceRecovery(npn, failed)
      this.announceError(failed)
      console.error("[npn-validate] failed", err)
    } finally {
      this.setButton(false)
    }
  }

  // Hands the recovery card (npn-recovery controller, listening on window) the
  // masked contacts plus the typed NPN — the card's endpoints re-resolve the
  // holder from that NPN server-side on every action. Every validate outcome
  // WITHOUT recovery data dispatches recovery-clear instead: a card armed by an
  // earlier attempt must not stay up acting on that stale NPN (its buttons
  // would email/text the PREVIOUS number's holder, not the corrected input).
  announceRecovery(npn, data) {
    if (!data.recovery) {
      this.dispatch("recovery-clear")
      return
    }

    this.dispatch("recovery", { detail: { npn, ...data.recovery } })
  }

  // Tells the onboarding wizard root a validation attempt failed so signup-support
  // can offer a support ticket. Event name: npn-validate:error (immediate) or
  // npn-validate:client-error (throttled). Harmless on the profile form, this
  // controller's other host, where nothing listens.
  announceError(data, eventName = "error") {
    if (data.valid) return

    this.dispatch(eventName, { detail: { message: (data.errors || []).join(" ") } })
  }

  // Built from DOM nodes (not innerHTML): server strings go in via textContent
  // and a typed href, so they never reach an HTML-parsing context.
  show(data) {
    if (!this.hasMessageTarget) return
    this.messageTarget.replaceChildren()

    if (data.valid) {
      this.messageTarget.className = "gl-onboarding-npn-message gl-onboarding-npn-message--valid"
      this.messageTarget.append(this.icon("ph-check-circle"), ` ${data.message || "NPN looks good."}`)
      // Non-blocking hint (#13619): the NPN is fine, but another account looks like this agent's.
      if (data.suggestion) this.messageTarget.append(this.suggestion(data.suggestion))
      return
    }

    // Server returns one sentence: format/length copy, the "taken" copy, or a generic
    // "Invalid NPN" for anything else (a denylist match is never revealed).
    this.messageTarget.className = "gl-onboarding-npn-message gl-onboarding-npn-message--invalid"
    this.messageTarget.append(this.icon("ph-warning-circle"), ` ${(data.errors || []).join(" ")}`)
    if (data.sign_in_url) {
      const link = document.createElement("a")
      link.setAttribute("href", data.sign_in_url)
      link.className = "gl-onboarding-link"
      link.textContent = "Sign in instead?"
      this.messageTarget.append(" ", link)
    }
  }

  suggestion(text) {
    const span = document.createElement("span")
    span.className = "gl-onboarding-npn-message__suggestion"
    span.textContent = text
    return span
  }

  icon(name) {
    const i = document.createElement("i")
    i.className = `ph-fill ${name}`
    return i
  }

  clearMessage() {
    if (!this.hasMessageTarget) return
    this.messageTarget.className = "gl-onboarding-npn-message d-none"
    this.messageTarget.replaceChildren()
  }

  // BUTTON-8: the label never swaps; busy is the shared spinner + aria-busy.
  setButton(busy) {
    if (!this.hasButtonTarget) return
    setButtonLoading(this.buttonTarget, busy)
  }
}
